/**
 * The rank grid, as pure data: types, geometry, bands and the summary numbers under a map.
 * No DOM and no Leaflet here, so it runs in the Worker, in tests and in the browser alike.
 *
 * Geometry: points are laid out on a square grid centred on `center`, `spacingMiles` apart, row 0 at the north.
 * One degree of latitude is 69.0 miles; one degree of longitude is 69.172 × cos(latitude) miles. At the 2.4-mile
 * spacing docs/12 §2 uses, the error of this flat approximation across a 19-mile grid is well under 0.1%.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/** A rank of 1 to 20 means the business was found at that position; null means it wasn't in the top 20. */
export type Rank = number | null;

export interface RankGridData {
  /** Schema version, so stored reports keep rendering when this package changes. */
  version: 1;
  /** Where the grid is centred. For client reports this is the shop's pin. */
  center: LatLng;
  /** Distance between neighbouring points, in miles. docs/12 §2: 2.4. */
  spacingMiles: number;
  /** Points per side. docs/12 §2: 9 (81 points). Must be odd so the centre is a point. */
  size: number;
  /** Row-major, north row first, west to east: size × size entries. */
  ranks: Rank[];
  /** The search that was measured, for example "water heater repair". */
  keyword: string;
  /** ISO date the grid was measured. */
  measuredAt: string;
  /** The business measured. Omit for anonymous samples. */
  business?: { name: string; pin?: LatLng };
  /** True for invented demo data. The component then labels the map as a sample, always. */
  sample?: boolean;
}

export type Band = 'top3' | 'near' | 'far' | 'none';

/** docs/12 §2 colours: 1 to 3 (in the map pack), 4 to 10, 11 to 20, not in the top 20. */
export function band(rank: Rank): Band {
  if (rank == null || rank > 20) return 'none';
  if (rank <= 3) return 'top3';
  if (rank <= 10) return 'near';
  return 'far';
}

export const BAND_LABEL: Record<Band, string> = {
  top3: '1 to 3',
  near: '4 to 10',
  far: '11 to 20',
  none: 'Not in the top 20',
};

const MILES_PER_DEG_LAT = 69.0;
const milesPerDegLng = (lat: number) => 69.172 * Math.cos((lat * Math.PI) / 180);

/** The lat/lng of every point, row-major from the north-west corner. */
export function gridPoints(center: LatLng, spacingMiles: number, size: number): LatLng[] {
  const half = (size - 1) / 2;
  const dLat = spacingMiles / MILES_PER_DEG_LAT;
  const dLng = spacingMiles / milesPerDegLng(center.lat);
  const out: LatLng[] = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      out.push({ lat: center.lat + (half - r) * dLat, lng: center.lng + (c - half) * dLng });
    }
  }
  return out;
}

/** South-west and north-east corners of the grid, padded by `padPoints` spacings on every side. */
export function gridBounds(center: LatLng, spacingMiles: number, size: number, padPoints = 0.5): [LatLng, LatLng] {
  const half = (size - 1) / 2 + padPoints;
  const dLat = (spacingMiles / MILES_PER_DEG_LAT) * half;
  const dLng = (spacingMiles / milesPerDegLng(center.lat)) * half;
  return [
    { lat: center.lat - dLat, lng: center.lng - dLng },
    { lat: center.lat + dLat, lng: center.lng + dLng },
  ];
}

export interface GridSummary {
  points: number;
  /** Points ranked 1 to 3. The number the day-90 promise measures (docs/01 §4). */
  top3: number;
  /** Share of points in the top three, 0 to 1. */
  top3Share: number;
  /** Points where the business appeared in the top 20 at all. */
  found: number;
  /** Mean rank over the points where it was found, or null if it was found nowhere. */
  averageFoundRank: number | null;
  counts: Record<Band, number>;
}

export function summarise(ranks: Rank[]): GridSummary {
  const counts: Record<Band, number> = { top3: 0, near: 0, far: 0, none: 0 };
  let sum = 0;
  let found = 0;
  for (const r of ranks) {
    const b = band(r);
    counts[b]++;
    if (b !== 'none' && r != null) {
      sum += r;
      found++;
    }
  }
  return {
    points: ranks.length,
    top3: counts.top3,
    top3Share: ranks.length ? counts.top3 / ranks.length : 0,
    found,
    averageFoundRank: found ? Math.round((sum / found) * 10) / 10 : null,
    counts,
  };
}

/** Throws with a plain message if the data can't be drawn honestly. */
export function validate(data: RankGridData): void {
  const problems: string[] = [];
  if (data.version !== 1) problems.push(`unknown version ${String(data.version)}`);
  if (!Number.isInteger(data.size) || data.size < 3 || data.size % 2 === 0) problems.push('size must be an odd integer of 3 or more');
  if (!(data.spacingMiles > 0 && data.spacingMiles <= 10)) problems.push('spacingMiles must be between 0 and 10');
  if (Math.abs(data.center?.lat ?? 999) > 85 || Math.abs(data.center?.lng ?? 999) > 180) problems.push('center is not a valid lat/lng');
  if (!Array.isArray(data.ranks) || data.ranks.length !== data.size * data.size) problems.push(`ranks must have ${data.size * data.size} entries`);
  else if (data.ranks.some((r) => r !== null && !(Number.isInteger(r) && r >= 1))) problems.push('each rank must be a whole number from 1, or null');
  if (!data.keyword?.trim()) problems.push('keyword is required');
  if (Number.isNaN(Date.parse(data.measuredAt))) problems.push('measuredAt must be an ISO date');
  if (problems.length) throw new Error(`Rank grid data is invalid: ${problems.join('; ')}`);
}

/** The label drawn in a cell: the rank, or "20+" when it wasn't found. */
export function cellLabel(rank: Rank): string {
  return band(rank) === 'none' ? '20+' : String(rank);
}

/** Compass words for a point relative to the centre, for table headers and screen-reader text. */
export function pointName(index: number, size: number): string {
  const half = (size - 1) / 2;
  const r = Math.floor(index / size);
  const c = index % size;
  return `row ${r + 1}, column ${c + 1}${r === half && c === half ? ' (center)' : ''}`;
}
